import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeIcon from "@mui/icons-material/Home";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { FriendlyTextField, FriendlyButton } from "../shared";

/**
 * DonorProfile Component
 * Displays and allows editing of donor profile information
 */
const DonorProfile = ({
  t,
  donorData,
  onUpdate,
  onContinue,
  isLoading = false,
  error = null,
  isCompact = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: donorData?.first_name || "",
    last_name: donorData?.last_name || "",
    phone: donorData?.phone || "",
    address: donorData?.adresse || donorData?.address || "",
    zip_code: donorData?.zip_code || "",
  });

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleCancel = () => {
    // Reset form data to original donor data
    setFormData({
      first_name: donorData?.first_name || "",
      last_name: donorData?.last_name || "",
      phone: donorData?.phone || "",
      address: donorData?.adresse || donorData?.address || "",
      zip_code: donorData?.zip_code || "",
    });
    setIsEditing(false);
  };

  const handleSave = async () => {
    await onUpdate(formData);
    setIsEditing(false);
  };

  if (!donorData) {
    return (
      <Alert severity="warning">
        {t("no_donor_data", "No donor profile found")}
      </Alert>
    );
  }

  // View Mode
  if (!isEditing) {
    return (
      <Box sx={{ maxWidth: 600, mx: "auto" }}>
        {!isCompact && (
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: "primary.100",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <PersonIcon sx={{ fontSize: 40, color: "primary.main" }} />
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}
            >
              {t("your_profile", "Your Profile")}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t("review_profile", "Review your information before continuing")}
            </Typography>
          </Box>
        )}

        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <PersonIcon color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("name", "Name")}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {donorData.first_name} {donorData.last_name}
                </Typography>
              </Box>
            </Box>

            <Divider />

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <EmailIcon color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("email", "Email")}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {donorData.email}
                </Typography>
              </Box>
            </Box>

            {donorData.phone && (
              <>
                <Divider />
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <PhoneIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("phone", "Phone")}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {donorData.phone}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}

            {(donorData.adresse || donorData.address || donorData.zip_code) && (
              <>
                <Divider />
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <HomeIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("address", "Address")}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {[donorData.adresse || donorData.address, donorData.zip_code].filter(Boolean).join(", ")}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Stack>
        </Paper>

        <Stack direction="row" spacing={2}>
          <FriendlyButton
            variant="outlined"
            onClick={() => setIsEditing(true)}
            startIcon={<EditIcon />}
            sx={{ flex: 1 }}
          >
            {t("edit_profile", "Edit Profile")}
          </FriendlyButton>
          <FriendlyButton
            onClick={onContinue}
            sx={{ flex: 1 }}
          >
            {t("continue", "Continue")}
            <ArrowForwardIcon sx={{ ml: 1 }} />
          </FriendlyButton>
        </Stack>
      </Box>
    );
  }

  // Edit Mode
  return (
    <Box sx={{ maxWidth: 600, mx: "auto" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        {!isCompact && (
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t("edit_profile", "Edit Profile")}
          </Typography>
        )}
        <IconButton
          onClick={handleCancel}
          color="error"
          aria-label={t("cancel", "Cancel")}
          sx={{ ml: "auto" }}
        >
          <CancelIcon />
        </IconButton>
      </Box>

      <Stack spacing={3}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
            width: "100%",
          }}
        >
          <FriendlyTextField
            label={t("first_name", "First Name")}
            value={formData.first_name}
            onChange={handleChange("first_name")}
            required
          />
          <FriendlyTextField
            label={t("last_name", "Last Name")}
            value={formData.last_name}
            onChange={handleChange("last_name")}
            required
          />
        </Box>

        <FriendlyTextField
          label={t("phone", "Phone Number")}
          value={formData.phone}
          onChange={handleChange("phone")}
        />

        <FriendlyTextField
          label={t("address", "Address")}
          value={formData.address}
          onChange={handleChange("address")}
        />

        <FriendlyTextField
          label={t("zip_code", "ZIP Code")}
          value={formData.zip_code}
          onChange={handleChange("zip_code")}
        />

        <FriendlyButton
          onClick={handleSave}
          disabled={isLoading || !formData.first_name?.trim() || !formData.last_name?.trim()}
          sx={{ width: "100%" }}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
        >
          {t("save", "Save")}
        </FriendlyButton>

        {error && (
          <Alert severity="error" sx={{ py: 1.5 }}>
            {error}
          </Alert>
        )}
      </Stack>
    </Box>
  );
};

export default DonorProfile;
